import React from 'react';

const PrivacyPolicy = () => {
  const contactEmail = 'hipproasy@gmail.com';
  const effectiveDate = 'September 2025';
  const appName = 'Pals';
  const developerName = 'Marasy Phi';

  return (
    <div>
      <h1>Privacy Policy</h1>
      <p>
        <strong>Effective Date:</strong> {effectiveDate}
      </p>
      <p>
        This Privacy Policy applies to the app <strong>{appName}</strong>, developed and maintained
        by <strong>{developerName}</strong>.
      </p>

      <p>
        We are committed to protecting your privacy and being transparent about how we handle your data.
        This Privacy Policy explains our privacy-first approach to building meaningful connections.
      </p>

      <h2 id="no-personal-data-collection">No Server Data Collection</h2>
      <p>
        <strong>{appName}</strong> is designed with privacy at its core. We do not collect, store, or have access
        to your personal network information on our servers.
      </p>
      <p>
        <strong>Information We Do Not Collect on Our Servers:</strong>
      </p>
      <ul>
        <li>Personal identifiers such as names or email addresses</li>
        <li>Your contact list or personal network information</li>
        <li>Photos or personal notes about your connections</li>
        <li>Location data or movement patterns</li>
        <li>Any sensitive personal information</li>
      </ul>

      <h2 id="local-data-handling">Local Data Handling</h2>
      <p>
        <strong>Storage of Information:</strong> All your personal network data is stored locally on
        your device and synced via Apple iCloud. This includes contact information, photos, notes,
        custom tags, and location data you choose to add.
      </p>
      <p>
        <strong>Use of Local Data:</strong> Your locally stored data is used solely to provide the
        core functionality of organizing and managing your personal network. This enables features
        like contact management, relationship insights, and nearby detection.
      </p>
      <p>
        <strong>iCloud Sync:</strong> Your data automatically syncs across your devices using Apple's
        iCloud service. This synchronization is handled entirely by Apple and follows their privacy
        and security standards.
      </p>
      <p>
        <strong>Data Removal:</strong> When you delete the app, your local data is removed from your
        device. iCloud data follows Apple's data retention policies and can be managed through your
        iCloud settings.
      </p>

      <h2 id="anonymous-analytics">Anonymous Analytics</h2>
      <p>
        We may collect limited, anonymous technical information to improve the app experience:
      </p>
      <ul>
        <li>Device type and operating system version (for compatibility)</li>
        <li>Anonymous app usage statistics (features used, performance metrics)</li>
        <li>Crash reports (containing no personal information)</li>
      </ul>
      <p>
        All analytics data is aggregated and anonymized. We cannot identify individual users from this information.
      </p>

      <h2 id="location-privacy">Location Privacy</h2>
      <p>
        <strong>Opt-in Only:</strong> Location access requires your explicit permission and is entirely optional.
      </p>
      <p>
        <strong>Local Storage:</strong> Location data you choose to add (like where you met someone)
        is stored locally on your device and synced via iCloud.
      </p>
      <p>
        <strong>Nearby Feature:</strong> The nearby pals detection feature uses your current location
        but does not transmit this data to our servers.
      </p>

      <h2 id="premium-features">Premium Features</h2>
      <p>
        <strong>{appName}</strong> offers premium subscriptions that unlock additional features like
        unlimited contacts, archive access, and advanced insights.
      </p>
      <p>
        <strong>Free Tier Limitations:</strong>
      </p>
      <ul>
        <li>Up to 10 contacts</li>
        <li>Up to 5 custom tags</li>
        <li>Basic app functionality</li>
      </ul>
      <p>
        <strong>Premium Benefits:</strong> Unlimited contacts and tags, archive access, nearby detection,
        advanced insights, and ad-free experience.
      </p>

      <h2 id="subscription-information">Subscription Information</h2>
      <p>
        All payments for premium subscriptions are processed securely by the Apple App Store.
        We do not collect or have access to any credit card information or payment details.
      </p>
      <p>
        Subscriptions are managed directly through your Apple App Store account settings. You can
        manage your subscription, including cancellation and auto-renewal preferences, by visiting
        your account settings on the App Store.
      </p>
      <p>
        We only receive subscription status information from Apple (active/inactive) to enable
        premium features. No payment details or personal information is shared with us.
      </p>

      <h2 id="data-security">Data Security</h2>
      <p>
        Since we don't store your personal data on our servers, there's no risk of server breaches
        exposing your information. Your data is protected by:
      </p>
      <ul>
        <li>Your device's built-in security features</li>
        <li>Apple's iCloud encryption and security measures</li>
        <li>Local data storage under your complete control</li>
      </ul>

      <h2 id="information-sharing">Information Sharing</h2>
      <p>
        We do <strong>NOT</strong> collect, store, sell, trade, or rent your personal information.
      </p>
      <p>
        Since we don't store personal data on our servers, we have no personal information to share.
        Any legal requests for user data would be directed to Apple for iCloud information.
      </p>

      <h2 id="childrens-privacy">Children's Privacy</h2>
      <p>
        <strong>{appName}</strong> is designed to be safe for all users. Since we do not collect
        or store any personal information on our servers, there are no special privacy concerns
        regarding children's data.
      </p>
      <p>
        All data created in the app (contacts, notes, photos) is stored locally on the user's
        device and remains under the user's or parent's control.
      </p>

      <h2 id="changes-to-privacy-policy">Changes to This Privacy Policy</h2>
      <p>
        We may update our Privacy Policy from time to time. We will notify users of any material
        changes by posting the new Privacy Policy in the app and updating the "Effective Date" at the top.
      </p>

      <h2 id="third-party-services">Third-Party Services</h2>
      <p>
        <strong>Apple Services:</strong> We use Apple's iCloud for data synchronization, App Store for
        payments, and Core Location for location services (with your permission).
      </p>
      <p>
        All third-party services follow their respective privacy policies and security standards.
      </p>

      <h2 id="eula">End User License Agreement (EULA)</h2>
      <p>
        Your use of the <strong>{appName}</strong> app is also governed by Apple's Licensed Application
        End User License Agreement. You can view the full EULA at:{' '}
        <a
          href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://www.apple.com/legal/internet-services/itunes/dev/stdeula/
        </a>
      </p>
      <p>
        By downloading and using <strong>{appName}</strong>, you agree to be bound by the terms of
        both this Privacy Policy and Apple's End User License Agreement.
      </p>

      <h2 id="contact-us">Contact Us</h2>
      <p>
        If you have any questions about this Privacy Policy or our data practices, please contact us at{' '}
        <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
      </p>

      <p>
        <strong>Your Consent:</strong> By using <strong>{appName}</strong>, you consent to this Privacy Policy.
        If you do not agree with any part of this policy, please do not use our app.
      </p>
    </div>
  );
};

export default PrivacyPolicy;
